/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useTranslation} from "react-i18next";
import {PackageItem} from "types/entities/PackageItem";
import clsx from "clsx";
import React from "react";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {translate} from "utils/utility";
import PackageUpsellItemPriceWithStock
    from "pages/Front/Booking/components/PackageUpsell/components/PackageUpsellItemPriceWithStock";
import {useTypedSelector} from "helpers/reducers";

interface GroupedPackageUpsellItemItemContainerProps {
    setSelectedPackageUpsell: () => void;
    className: string;
    packageItem: PackageItem;
    inputs: React.ReactNode | React.ReactNode[];
    showRemoveButton: boolean;
    contained?: PackageItem | boolean;
    callToAction?: boolean;
    style: any;
    children?: React.ReactNode | React.ReactNode[];
    isGroupedProduct?: boolean;
    isLoading?: boolean;
    quantity?: number;
    isSeller?: boolean;
}

export const GroupedPackageUpsellItemItemContainer = (props: GroupedPackageUpsellItemItemContainerProps) => {
    const {t} = useTranslation();
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const dependsOn = props.packageItem?.extra?.depends_on_article_id ?? [];
    const disableButtonIfDontHaveArticleInCart = dependsOn.length > 0 && !selectedUpsells.some(upsell => dependsOn.includes(upsell.packageItem.article_id))
    const isButtonDisabled = Boolean(props.contained && !props.showRemoveButton) || disableButtonIfDontHaveArticleInCart;

    return (
        <div
            className={clsx(
                "GroupedPackageUpsellItemItemContainer flex flex-col sm:flex-row justify-between items-stretch w-full px-4 lg:pl-5 md:pr-2 py-3 gap-y-4 lg:gap-y-0",
                props.className,
            )}
        >
            <div className={clsx(
                "flex flex-row justify-between basis-full sm:basis-3/4 grow gap-2",
            )}>
                <h3 className={clsx(
                    "w-full pr-4 text-gray-900 md:text-lg xl:text-xl font-bold lg:font-medium self-start md:self-center",
                    props.isSeller ? "lg:w-[50%] xl:w-[50%]" : "lg:w-[60%] xl:w-[70%]",
                )}>
                    {translate(props.packageItem.name)}
                </h3>

                <div className={"flex flex-col gap-2 sm:flex-row"}>
                {props.packageItem?.extra?.max_count === 1 && (props.packageItem.minimum_persons_count || props.packageItem.maximum_persons_count > 0) && (
                    <div className={"flex flex-col self-center"}>
                        <span className={"p-1 bg-yellow-400 rounded-lg text-xs text-center"}>
                            {props.packageItem.maximum_persons_count === 500
                                ? t("personsminimum", {count: props.packageItem.minimum_persons_count})
                                : t("personsmin_max", {min: props.packageItem.minimum_persons_count, max: props.packageItem.maximum_persons_count})
                            }
                        </span>
                    </div>
                )}

                <div className={"flex flex-col"}>
                    {props.inputs}
                </div>

                {props.isGroupedProduct ? (
                    <div
                        className={clsx(props.isSeller ? "w-full lg:w-[50%]" : "flex-grow w-full lg:w-[40%] xl:w-[30%]")}>
                        {t("Included in Package")}
                    </div>
                ) : (
                    <PackageUpsellItemPriceWithStock
                        className={clsx(
                            "lg:!flex-row-reverse self-center lg:items-center lg:justify-start lg:gap-x-4 shrink basis-1/4",
                        )}
                        classNamePrice={props.isSeller ? "w-full  pr-2" : "w-full lg:w-1/2 text-right md:text-center "}
                        classNameStock={props.isSeller ? "w-full lg:w-1/3" : "w-full lg:w-1/2 text-center"}
                        classNameDiscount={props.isSeller ? "w-full " : ""}
                        isSeller={props.isSeller}
                        packageItem={props.packageItem}
                        quantity={props?.quantity}
                    />
                )}
                </div>
            </div>

            <div className={clsx(
                "flex flex-row items-center justify-end gap-4 basis-1/5 shrink",
            )}>
                {!props.isGroupedProduct && (
                    <div className={"w-full md:w-[180px] flex flex-col justify-center items-end md:items-center"}>
                        <ButtonSubmit
                            buttonType={props.showRemoveButton ? "selectedBlack" : "arbo-orange"}
                            callToAction={props.callToAction}
                            className={clsx(
                                "!w-auto grow rounded-lg !px-2 !py-1 xl:!py-1.5 xl:!px-2 !text-lg xl:!text-xl !font-bold",
                            )}
                            disabled={isButtonDisabled}
                            icon={props.showRemoveButton ? "check" : "plusChar"}
                            id={"ButtonSelectUpsell"}
                            label={props.showRemoveButton ? t("Selected") : t("Add")}
                            onClick={props.setSelectedPackageUpsell}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
